<template>
	<highcharts class="mb-100" :options="chartOptions"></highcharts>
</template>

<script setup lang="ts">
import { Axis, CSSObject, Options, PlotLineDataLabelsOptions } from 'highcharts';
import { computed, ref } from 'vue';
import BaseChart from './BaseChartOptions';
import deepMerge from 'deepmerge';
import { Chart as highcharts } from 'highcharts-vue';

const chart = ref<Highcharts.Chart | null>(null);
const whiteBlock = ref<Highcharts.SVGElement | undefined>(undefined);
const arrowBlack = ref<Highcharts.SVGElement | undefined>(undefined);
const arrowBlueRect = ref<Highcharts.SVGElement | undefined>(undefined);
const arrowBlueTriangle = ref<Highcharts.SVGElement | undefined>(undefined);
const arrowTextTop = ref<Highcharts.SVGElement | undefined>(undefined);
const arrowTextBottom = ref<Highcharts.SVGElement | undefined>(undefined);

const chartOptions = computed((): Options => {
	const averageIPOVerticalDataLabels: PlotLineDataLabelsOptions = {
		enabled: true,
		useHTML: true,
		format: '<div style="text-align:center">{point.name}</div>',
		allowOverlap: true,
		style: {
			fontFamily: '"Ivar Headline", "Source Sans Pro", "Helvetica Neue", "Helvetica", "Arial", sans-serif',
			fontSize: '12px'
		},
		align: 'center'
	};
	const options: Options = {
		chart: {
			type: 'areaspline',
			backgroundColor: 'white',
			spacing: [20, 10, 20, 5],
			height: 410,
			events: {
				render: renderArrowAndText
			}
		},
		plotOptions: {
			areaspline: {
				marker: {
					enabled: false
				}
			},
			scatter: {
				marker: {
					symbol: 'circle'
				}
			},
			series: {
				enableMouseTracking: false
			}
		},
		xAxis: [
			{
				title: {
					text: 'Age in years',
					style: {
						fontWeight: '700'
					}
				},
				startOnTick: false,
				min: 0,
				max: 21.1,
				endOnTick: false,
				tickInterval: 5,
				gridLineWidth: 1,
				gridLineColor: 'rgba(199, 199, 199, 1)',
				gridZIndex: 1
			},
			{
				startOnTick: false,
				min: 0,
				max: 21.1,
				endOnTick: false,
				tickInterval: 5,
				tickColor: 'black',
				lineColor: 'black',
				linkedTo: 0,
				labels: {
					enabled: false
				},
				offset: 0
			}
		],
		yAxis: [
			{
				title: {
					text: 'Inflation adjusted revenue ($M)',
					style: {
						fontWeight: '700'
					}
				},
				tickInterval: 100,
				startOnTick: false,
				endOnTick: true,
				max: 300,
				gridLineColor: 'rgba(199, 199, 199, 1)',
				labels: {
					x: -5
				}
			},
			{
				offset: 0,
				max: 300,
				lineColor: 'black',
				linkedTo: 0,
				lineWidth: 1,
				labels: {
					enabled: false
				},
				title: {
					text: null
				}
			}
		],
		series: [
			{
				id: 'splinetop',
				name: 'splinetop',
				zoneAxis: 'y',
				type: 'areaspline',
				xAxis: 0,
				yAxis: 0,
				color: 'rgba(210, 228, 255, 1)',
				lineWidth: 0,
				zIndex: -1,
				zones: [
					{
						value: 180,
						color: 'rgba(231, 231, 231, 1)'
					}
				],
				data: [
					[3, 0],
					[4, 1],
					[6, 15],
					[9, 80],
					[10, 110],
					[12, 190],
					[15, 265],
					[20, 300],
					[20.8, 300]
				]
			},
			{
				id: 'revenueLine',
				name: 'revenue line',
				type: 'line',
				dashStyle: 'ShortDash',
				color: 'rgba(121, 125, 127, .5)',
				marker: {
					enabled: false
				},
				data: [
					[11.7, 180],
					[21, 180]
				],
				zIndex: 99
			},
			{
				id: 'grayScatter',
				name: 'grayScatter',
				type: 'scatter',
				color: 'rgba(153, 153, 153, 1)',
				data: [
					[4, 15],
					[7, 25],
					[7, 30],
					[8, 30],
					[8, 35],
					[8, 40],
					[8.5, 55],
					[9, 60]
				]
			},
			{
				id: 'blueScatter',
				name: 'blueScatter',
				type: 'scatter',
				color: 'rgba(0, 128, 255, 1)',
				data: [
					[11, 188],
					[12, 189],
					[12, 184],
					[12, 173],
					[13, 180]
				]
			},
			{
				id: 'averageIPO1990vertical',
				name: 'averageIPO1990vertical',
				type: 'line',
				color: 'black',
				marker: {
					enabled: false
				},
				dataLabels: averageIPOVerticalDataLabels,
				data: [
					{ x: 7.3, y: 0 },
					{
						x: 7.3,
						y: 240,
						name: 'Average<br/>IPO<br/>(1990s)'
					}
				]
			},
			{
				id: 'averageIPO1990horizontal',
				name: 'averageIPO1990horizontal',
				type: 'line',
				color: 'black',
				marker: {
					enabled: false
				},
				data: [
					[6.7, 40],
					[7.9, 40]
				]
			},
			{
				id: 'averageIPO2021vertical',
				name: 'averageIPO2021vertical',
				type: 'line',
				color: 'black',
				marker: {
					enabled: false
				},
				dataLabels: averageIPOVerticalDataLabels,
				data: [
					{ x: 12, y: 0 },
					{
						x: 12,
						y: 240,
						name: "Average<br/>IPO<br/>('17-21)"
					}
				]
			},
			{
				id: 'averageIPO2021horizontal',
				name: 'averageIPO2021horizontal',
				type: 'line',
				color: 'black',
				marker: {
					enabled: false
				},
				data: [
					[11.4, 180],
					[12.6, 180]
				]
			}
		],
		responsive: {
			rules: [
				{
					condition: {
						minWidth: 480
					},
					chartOptions: {
						chart: {
							spacing: [20, 120, 20, 20]
						},
						xAxis: [{ max: 24 }, { max: 24, tickInterval: 1 }],
						series: [
							{
								id: 'splinetop',
								type: 'areaspline'
							},
							{
								id: 'revenueLine',
								type: 'line',
								data: [
									[11.7, 180],
									[24, 180]
								]
							},
							{
								id: 'grayScatter',
								name: 'grayScatter',
								type: 'scatter'
							},
							{
								id: 'blueScatter',
								name: 'blueScatter',
								type: 'scatter'
							},
							{
								id: 'averageIPO1990vertical',
								name: 'averageIPO1990vertical',
								type: 'line',
								dataLabels: {
									style: {
										fontSize: '16px'
									}
								},
								data: [
									{ x: 7.3, y: 0 },
									{
										x: 7.3,
										y: 240,
										name: 'Average IPO<br/>(1990s)'
									}
								]
							},
							{
								id: 'averageIPO1990horizontal',
								type: 'line'
							},
							{
								id: 'averageIPO2021vertical',
								name: 'averageIPO2021vertical',
								type: 'line',
								dataLabels: {
									style: {
										fontSize: '16px'
									}
								},
								data: [
									{ x: 12, y: 0 },
									{
										x: 12,
										y: 240,
										name: "Average IPO<br/>('17-21)"
									}
								]
							},
							{
								id: 'averageIPO2021horizontal',
								type: 'line'
							}
						]
					}
				},
				{
					condition: {
						minWidth: 480,
						maxWidth: 700
					},
					chartOptions: {
						series: [
							{
								id: 'splinetop',
								type: 'areaspline'
							},
							{
								id: 'revenueLine',
								type: 'line'
							},
							{
								id: 'grayScatter',
								name: 'grayScatter',
								type: 'scatter'
							},
							{
								id: 'blueScatter',
								name: 'blueScatter',
								type: 'scatter'
							},
							{
								id: 'averageIPO1990vertical',
								name: 'averageIPO1990vertical',
								type: 'line',
								dataLabels: averageIPOVerticalDataLabels,
								data: [
									{ x: 7.3, y: 0 },
									{
										x: 7.3,
										y: 240,
										name: 'Average<br/>IPO<br/>(1990s)'
									}
								]
							},
							{
								id: 'averageIPO1990horizontal',
								type: 'line'
							},
							{
								id: 'averageIPO2021vertical',
								type: 'line',
								dataLabels: averageIPOVerticalDataLabels,
								data: [
									{ x: 12, y: 0 },
									{
										x: 12,
										y: 240,
										name: "Average<br/>IPO<br/>('17-21)"
									}
								]
							},
							{
								id: 'averageIPO2021horizontal',
								type: 'line'
							}
						]
					}
				}
			]
		}
	};

	return deepMerge(new BaseChart().getOptions(), options, { arrayMerge: (dest, source) => source });
});

function objectIsEmpty(obj: object): boolean {
	return Object.keys(obj).length === 0 && obj.constructor === Object;
}

function addWhiteBlock(arrowPosX: number) {
	if (whiteBlock.value && !objectIsEmpty(whiteBlock.value)) {
		whiteBlock.value.destroy();
	}

	whiteBlock.value = chart.value?.renderer.rect(arrowPosX, 0, 399, 500).attr({ fill: 'white', zIndex: 2 }).add();
}

function addArrowBlack(mainYAxis: Axis, arrowPosX: number, arrowMarginLeft: number, arrowWidth: number) {
	if (arrowBlack.value && !objectIsEmpty(arrowBlack.value)) {
		arrowBlack.value.destroy();
	}

	const arrowBlackPosY = mainYAxis.toPixels(mainYAxis.min as number, false);
	const arrowBlackCutOffPosY = chart.value?.series[0].data[5].y || 0;
	const arrowBlackHeight = arrowBlackCutOffPosY + 5;

	arrowBlack.value = chart.value?.renderer
		.rect(arrowPosX + arrowMarginLeft, arrowBlackPosY - arrowBlackHeight, arrowWidth, arrowBlackHeight)
		.attr({ fill: 'black', zIndex: 3 })
		.add();
}

function addArrowBlue(mainYAxis: Axis, arrowPosX: number, arrowMarginLeft: number, arrowWidth: number) {
	const arrowBluePosY = mainYAxis.toPixels(183, false);
	const arrowBlueCutOffPosY = 98;
	if (arrowBlueRect.value && !objectIsEmpty(arrowBlueRect.value)) {
		arrowBlueRect.value.destroy();
	}
	if (arrowBlueTriangle.value && !objectIsEmpty(arrowBlueTriangle.value)) {
		arrowBlueTriangle.value.destroy();
	}
	arrowBlueRect.value = chart.value?.renderer
		.rect(arrowPosX + arrowMarginLeft, arrowBluePosY - arrowBlueCutOffPosY, arrowWidth, arrowBlueCutOffPosY)
		.attr({ fill: 'rgba(0, 128, 255, 1)', zIndex: 3 })
		.add();

	arrowBlueTriangle.value = chart.value?.renderer
		.path([])
		.attr({
			d: 'M 50,20 95,95 5,95 z',
			fill: 'rgba(0, 128, 255, 1)',
			zIndex: 3,
			transform: 'translate(' + arrowPosX + ',' + (arrowBluePosY - arrowBlueCutOffPosY - 36) + ') scale(.4)'
		})
		.add();
}

function addArrowText(x: number, y: number, text: string, css: CSSObject, isTop: boolean) {
	if (isTop) {
		if (arrowTextTop.value && !objectIsEmpty(arrowTextTop.value)) {
			arrowTextTop.value.destroy();
		}
		arrowTextTop.value = chart.value?.renderer.text(text, x, y, true).attr({ zIndex: 3 }).css(css).add();
	} else {
		if (arrowTextBottom.value && !objectIsEmpty(arrowTextBottom.value)) {
			arrowTextBottom.value.destroy();
		}
		arrowTextBottom.value = chart.value?.renderer.text(text, x, y, true).attr({ zIndex: 3 }).css(css).add();
	}
}

function getArrowParams(chartWidth: number, mainXAxis: Axis, mainYAxis: Axis) {
	let arrowPosX = -9999;
	let x = chartWidth - 120;
	let topY = mainYAxis.toPixels(220, false);
	const topText = '<div>Individuals only<br/>able to participate<br/>after IPO</div>';
	let botY = mainYAxis.toPixels(160, false);
	let botText = '<div>Venture capital<br/>consumes most<br/>of the upside<br/>pre-IPO</div>';
	let fontSize = '12px';
	let css = {
		fontSize,
		color: 'rgba(121, 125, 127, 1)',
		fontWeight: '600'
	};
	let topCss = { ...css, color: 'rgba(0, 128, 255, 1)' };

	if (chartWidth >= 480) {
		arrowPosX = mainXAxis.toPixels(21, false);
		topY = mainYAxis.toPixels(245, false);
		botY = mainYAxis.toPixels(115, false);
		botText = '<div style="text-align:right">Venture capital<br/>consumes most<br/>of the upside<br/>pre-IPO</div>';
		fontSize = '14px';
		css = { ...css, fontSize };
		topCss = css;
	}

	if (chartWidth > 700) {
		x = chartWidth - 140;
	}

	return {
		arrowPosX,
		marginLeft: 10,
		width: 20,
		top: {
			x,
			y: topY,
			text: topText,
			css: topCss
		},
		bot: {
			x,
			y: botY,
			text: botText,
			css
		}
	};
}

function renderArrowAndText(event: Event): void {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	chart.value = event.target as any as Highcharts.Chart;

	const mainYAxis = chart.value.yAxis[0];
	const mainXAxis = chart.value.xAxis[0];
	const arrowParams = getArrowParams(chart.value.chartWidth, mainXAxis, mainYAxis);

	addWhiteBlock(arrowParams.arrowPosX);
	addArrowBlack(mainYAxis, arrowParams.arrowPosX, arrowParams.marginLeft, arrowParams.width);
	addArrowBlue(mainYAxis, arrowParams.arrowPosX, arrowParams.marginLeft, arrowParams.width);
	addArrowText(arrowParams.top.x, arrowParams.top.y, arrowParams.top.text, arrowParams.top.css, true);
	addArrowText(arrowParams.bot.x, arrowParams.bot.y, arrowParams.bot.text, arrowParams.bot.css, false);
}
</script>
